import { notification } from 'antd';
import React from 'react';

enum NotificationType {
  Info = 'info',
  Error = 'error',
  Success = 'success',
}

export interface NotifyOptions {
  /**
   * Root `data-testid` for the notification container.
   * The `message` is wrapped with `data-testid="${testId}__Title"`
   * and the `description` with `data-testid="${testId}__Description"`.
   */
  testId?: string;
}

interface WrapWithTestIdArgs {
  testId: string | undefined;
  suffix: 'Title' | 'Description';
  content: React.ReactNode;
}

const wrapWithTestId = ({ testId, suffix, content }: WrapWithTestIdArgs): React.ReactNode => {
  if (!testId || content == null || content === '') return content;

  return <span data-testid={`${testId}__${suffix}`}>{content}</span>;
};

interface NotifyArgs {
  type: NotificationType;
  message: React.ReactNode;
  description?: React.ReactNode;
  options?: NotifyOptions;
}

const notify = ({ type, message, description, options }: NotifyArgs) => {
  const { testId } = options || {};

  notification[type]({
    message: wrapWithTestId({ testId, suffix: 'Title', content: message }),
    description: wrapWithTestId({ testId, suffix: 'Description', content: description }),
    ...(testId ? { props: { 'data-testid': testId } } : {}),
  });
};

const info = (message: React.ReactNode, description?: string, options?: NotifyOptions) => {
  notify({ type: NotificationType.Info, message, description, options });
};

const error = (message: React.ReactNode = 'Something went wrong', description?: string, options?: NotifyOptions) => {
  notify({ type: NotificationType.Error, message, description, options });
};

const success = (message: React.ReactNode, description?: string, options?: NotifyOptions) => {
  notify({ type: NotificationType.Success, message, description, options });
};

const Notification = {
  Info: info,
  Error: error,
  Success: success,
};

export default Notification;
